import { createContext, useContext, useMemo, useReducer } from 'react';
import { ROLE } from 'utils/constants';
import { loadSelectedClinicId } from 'utils/storage';
type Action =
  | { type: 'login'; payload: UserProfile }
  | { type: 'getUserProfile'; payload: UserProfile }
  | { type: 'changeClinic'; payload: { clinicId: string } }
  | { type: 'logout' };

type Dispatch = (action: Action) => void;

export interface ClinicLocation {
  id: string;
  city: string;
  state: string;
  country: string;
}

export interface AdminRole {
  role: string;
  firstName: string;
  lastName: string;
  name: string;
  clinicGroup?: ClinicGroup;
}

interface Clinic {
  id: string;
  name: string;
  avatar: string;
  timezone: string;
  location: ClinicLocation;
  clinicGroup?: ClinicGroup;
}

export interface ClinicGroup {
  id: string;
  name: string;
  slug: string;
}

export interface ClinicUserRole {
  id: string;
  role: string;
  status: string;
  firstName: string;
  lastName: string;
  name: string;
  clinic: Clinic;
}

export interface UserProfile {
  id: string;
  email: string;
  clinicUserRoles: ClinicUserRole[];
  currentClinicUserRole: ClinicUserRole | null;
  adminRole: AdminRole | null;
}

interface UserProviderProps {
  children: React.ReactNode;
}

const UserContext = createContext<{
  user: UserProfile;
  dispatch: Dispatch;
} | null>(null);

const initialState = {
  id: '',
  email: '',
  clinicUserRoles: [],
  currentClinicUserRole: null,
  adminRole: null,
};

export const getAdminRole = (clinicUserRoles: ClinicUserRole[]) => {
  const ADMIN_ROLES = [ROLE.GLOBAL_ADMIN, ROLE.GROUP_ADMIN];

  const adminRow = clinicUserRoles.find((clinicUserRole) =>
    ADMIN_ROLES.includes(clinicUserRole.role)
  );

  if (!adminRow) {
    return null;
  }

  return {
    role: adminRow.role,
    firstName: adminRow.firstName,
    lastName: adminRow.lastName,
    name: adminRow.name,
    clinicGroup: adminRow.clinic?.clinicGroup,
  };
};

const userReducer = (state: UserProfile, action: Action) => {
  const clinicId = loadSelectedClinicId();

  switch (action.type) {
    case 'login':
    case 'getUserProfile': {
      const { clinicUserRoles } = action.payload;
      const adminRole = getAdminRole(clinicUserRoles);

      let currentClinicUserRole: null | ClinicUserRole =
        clinicUserRoles.find(
          (clinicUserRole) => clinicUserRole.clinic?.id === clinicId
        ) ?? null;

      // Priority: clinic view (only if clinicId is valid) > admin view > default first clinic
      if (!currentClinicUserRole) {
        currentClinicUserRole = adminRole ? null : clinicUserRoles[0];
      }

      return {
        ...state,
        ...action.payload,
        adminRole,
        currentClinicUserRole: currentClinicUserRole,
      };
    }
    case 'changeClinic': {
      const currentClinicUserRole = state.clinicUserRoles.find(
        (clinicUserRole) => clinicUserRole.clinic?.id === clinicId
      )!;

      return {
        ...state,
        currentClinicUserRole,
      };
    }
    case 'logout': {
      return initialState;
    }
    default: {
      throw new Error(`Unhandled action type`);
    }
  }
};

const UserProvider = ({ children }: UserProviderProps) => {
  const [user, dispatch] = useReducer(userReducer, initialState);

  const value = useMemo(
    () => ({
      user,
      dispatch,
    }),
    [user]
  );

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within UserProvider');
  }
  return context;
};

export { UserProvider, useUser };
